import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Trash2, Tag, Brain, Loader2, RefreshCw, Pencil, History, X, Check, ArrowRight } from 'lucide-react';
import { useMemoryStats } from '../hooks';
import { agentApi } from '../api';
import type { AgentMemory, MemoryHistoryItem } from '../api/agent';
import { usersApi, UserListItem } from '../api/users';
import { formatDate, formatDateTime } from '../utils/date';

const getTagStyle = (tag: string): string => {
  const hash = tag.split('').reduce((a, b) => {
    a = (a << 5) - a + b.charCodeAt(0);
    return a & a;
  }, 0);

  const colors = [
    'bg-purple-50 text-purple-700',
    'bg-orange-50 text-orange-700',
    'bg-blue-50 text-blue-700',
    'bg-green-50 text-green-700',
    'bg-pink-50 text-pink-700',
    'bg-teal-50 text-teal-700',
  ];

  return colors[Math.abs(hash) % colors.length];
};

const getCategoryStyle = (category: string): string => {
  switch (category) {
    case 'fact': return 'bg-blue-50 text-blue-700';
    case 'preference': return 'bg-purple-50 text-purple-700';
    case 'plan': return 'bg-amber-50 text-amber-700';
    case 'state': return 'bg-green-50 text-green-700';
    default: return 'bg-slate-50 text-slate-600';
  }
};

const eventBadge = (event: string) => {
  switch (event) {
    case 'ADD':
      return 'bg-green-100 text-green-700';
    case 'UPDATE':
      return 'bg-blue-100 text-blue-700';
    case 'DELETE':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};

const Memory: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get('userId') || '';

  const paramsRef = React.useRef<Record<string, string>>({});
  paramsRef.current = Object.fromEntries(searchParams.entries());

  const updateUrlParams = (updates: Record<string, string | null>) => {
    const merged = { ...paramsRef.current, ...updates };
    const params: Record<string, string> = {};
    for (const [k, v] of Object.entries(merged)) {
      if (v) params[k] = v;
    }
    paramsRef.current = params;
    setSearchParams(params, { replace: true });
  };

  const [searchQuery, setSearchQuery] = useState('');
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useMemoryStats();

  // Tab: 'memories' | 'history'
  const [activeTab, setActiveTab] = useState<'memories' | 'history'>('memories');

  // Users
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [selectedUserId, setSelectedUserIdState] = useState<string>(userIdFromUrl);
  const setSelectedUserId = (userId: string) => {
    setSelectedUserIdState(userId);
    updateUrlParams({ userId: userId || null });
  };

  // Memories
  const [memories, setMemories] = useState<AgentMemory[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // History
  const [history, setHistory] = useState<MemoryHistoryItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  // Delete state
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Load users on mount
  useEffect(() => {
    usersApi
      .list({ limit: 100 })
      .then((res) => setUsers(res.users))
      .catch(console.error);
  }, []);

  // Load memories
  const fetchMemories = async () => {
    if (!selectedUserId) {
      setMemories([]);
      return;
    }
    setIsLoading(true);
    try {
      const response = await agentApi.listMemories(selectedUserId);
      setMemories(response.memories);
    } catch (err) {
      console.error('Failed to fetch memories:', err);
      setMemories([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load history
  const fetchHistory = async () => {
    if (!selectedUserId) {
      setHistory([]);
      setHistoryTotal(0);
      return;
    }
    setIsHistoryLoading(true);
    try {
      const response = await agentApi.listMemoryHistory(selectedUserId, { limit: 100 });
      setHistory(response.history);
      setHistoryTotal(response.pagination.total);
    } catch (err) {
      console.error('Failed to fetch history:', err);
      setHistory([]);
      setHistoryTotal(0);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchMemories();
    if (activeTab === 'history') fetchHistory();
  }, [selectedUserId]);

  useEffect(() => {
    if (activeTab === 'history' && selectedUserId) fetchHistory();
  }, [activeTab]);

  const refetch = () => {
    fetchMemories();
    if (activeTab === 'history') fetchHistory();
  };

  // Filter
  const filteredMemories = searchQuery
    ? memories.filter((m) => m.content.toLowerCase().includes(searchQuery.toLowerCase()))
    : memories;
  const totalMemories = filteredMemories.length;

  const handleDelete = async (memoryId: string) => {
    if (!selectedUserId) return;
    setDeletingId(memoryId);
    try {
      await agentApi.deleteMemory(selectedUserId, memoryId);
      setShowDeleteConfirm(null);
      refetch();
      refetchStats();
    } catch (err) {
      console.error('Delete error:', err);
      alert(`Failed to delete memory: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (mem: AgentMemory) => {
    setEditingId(mem.memory_id);
    setEditContent(mem.content);
  };

  const handleEditSave = async () => {
    if (!selectedUserId || !editingId || !editContent.trim()) return;
    setIsSaving(true);
    try {
      await agentApi.updateMemory(selectedUserId, editingId, editContent.trim());
      setEditingId(null);
      setEditContent('');
      refetch();
    } catch (err) {
      console.error('Update error:', err);
      alert(`Failed to update memory: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditContent('');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Long-term Memory</h2>
          {!statsLoading && stats && (
            <p className="text-sm text-slate-500 mt-1">
              {stats.total_memories} memories across {stats.users_with_memories} users
            </p>
          )}
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
          >
            <option value="">Select User...</option>
            {users.map((user) => (
              <option key={user.user_id} value={user.user_id}>
                {user.user?.name || user.user_id} ({user.tier?.id || 'free'})
              </option>
            ))}
          </select>
          <button
            onClick={() => refetch()}
            className="bg-slate-100 text-slate-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-slate-200 flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        <button
          onClick={() => setActiveTab('memories')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'memories'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Brain size={16} />
          Memories
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'history'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <History size={16} />
          History
          {historyTotal > 0 && (
            <span className="bg-slate-200 text-slate-600 text-xs px-1.5 py-0.5 rounded-full">{historyTotal}</span>
          )}
        </button>
      </div>

      {/* Memories Tab */}
      {activeTab === 'memories' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
            <div className="relative w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Search memories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <span className="text-sm text-slate-500">
              {totalMemories} {totalMemories === 1 ? 'memory' : 'memories'} found
            </span>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Loader2 size={32} className="animate-spin mx-auto mb-4 text-blue-500" />
                <p className="text-slate-500">Loading memories...</p>
              </div>
            </div>
          ) : memories.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-slate-400">
              <div className="text-center">
                <Brain size={48} className="mx-auto mb-4 opacity-50" />
                <p>{selectedUserId ? 'No memories stored yet' : 'Select a user to view memories'}</p>
              </div>
            </div>
          ) : (
            <table className="w-full text-left">
              <thead className="bg-white border-b border-slate-100">
                <tr>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Tags</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Content</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Created</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredMemories.map((mem: AgentMemory) => (
                  <tr key={mem.memory_id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {mem.tags && mem.tags.length > 0 ? (
                          mem.tags.slice(0, 3).map((tag, idx) => (
                            <span
                              key={idx}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getTagStyle(tag)}`}
                            >
                              <Tag size={10} /> {tag}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-slate-400">No tags</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {mem.category ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getCategoryStyle(mem.category)}`}>
                          {mem.category}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">---</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 max-w-lg">
                      {editingId === mem.memory_id ? (
                        <div className="flex items-center gap-2">
                          <textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            rows={2}
                            className="flex-1 px-3 py-2 border border-blue-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 resize-none"
                          />
                          <button
                            onClick={handleEditSave}
                            disabled={isSaving || !editContent.trim()}
                            className="p-2 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                            title="Save"
                          >
                            {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                          </button>
                          <button
                            onClick={handleEditCancel}
                            className="p-2 text-slate-400 hover:bg-slate-100 rounded"
                            title="Cancel"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap">{mem.content}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">{formatDate(mem.created_at)}</td>
                    <td className="px-6 py-4 text-right">
                      {showDeleteConfirm === mem.memory_id ? (
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-xs text-slate-500">Delete?</span>
                          <button
                            onClick={() => handleDelete(mem.memory_id)}
                            disabled={deletingId === mem.memory_id}
                            className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                          >
                            {deletingId === mem.memory_id ? <Loader2 size={12} className="animate-spin" /> : 'Yes'}
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm(null)}
                            className="px-2 py-1 text-xs bg-slate-200 text-slate-700 rounded hover:bg-slate-300"
                          >
                            No
                          </button>
                        </div>
                      ) : editingId !== mem.memory_id ? (
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => handleEdit(mem)}
                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm(mem.memory_id)}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {isHistoryLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Loader2 size={32} className="animate-spin mx-auto mb-4 text-blue-500" />
                <p className="text-slate-500">Loading history...</p>
              </div>
            </div>
          ) : history.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-slate-400">
              <div className="text-center">
                <History size={48} className="mx-auto mb-4 opacity-50" />
                <p>{selectedUserId ? 'No history yet' : 'Select a user to view history'}</p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {history.map((item) => (
                <div key={item.id} className="px-6 py-4 hover:bg-slate-50">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${eventBadge(item.event)}`}>
                      {item.event}
                    </span>
                    <span className="text-xs text-slate-400">{formatDateTime(item.created_at)}</span>
                    <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">{item.source}</span>
                    {typeof item.extra_data?.old_category === 'string' && typeof item.extra_data?.new_category === 'string' && (
                      <span className="text-xs text-slate-500">
                        <span className={`px-1.5 py-0.5 rounded ${getCategoryStyle(item.extra_data.old_category)}`}>{item.extra_data.old_category}</span>
                        <ArrowRight size={10} className="inline mx-1 text-slate-300" />
                        <span className={`px-1.5 py-0.5 rounded ${getCategoryStyle(item.extra_data.new_category)}`}>{item.extra_data.new_category}</span>
                      </span>
                    )}
                    {typeof item.extra_data?.category === 'string' && !item.extra_data?.old_category && (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${getCategoryStyle(item.extra_data.category)}`}>{item.extra_data.category}</span>
                    )}
                    <span className="text-xs font-mono text-slate-400 ml-auto break-all">{item.memory_id}</span>
                  </div>

                  {item.event === 'ADD' && item.new_content && (
                    <p className="text-sm text-slate-600 pl-2 border-l-2 border-green-300">{item.new_content}</p>
                  )}

                  {item.event === 'UPDATE' && (
                    <div className="flex items-start gap-2 text-sm">
                      <p className="text-slate-400 line-through flex-1 pl-2 border-l-2 border-slate-200">
                        {item.old_content}
                      </p>
                      <ArrowRight size={14} className="text-slate-300 mt-1 shrink-0" />
                      <p className="text-slate-600 flex-1 pl-2 border-l-2 border-blue-300">{item.new_content}</p>
                    </div>
                  )}

                  {item.event === 'DELETE' && item.old_content && (
                    <p className="text-sm text-slate-400 line-through pl-2 border-l-2 border-red-300">
                      {item.old_content}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Memory;
